import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useI18n, formatCurrency, formatDateTime } from '@/lib/i18n';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Package, Trash2, ArrowDownCircle, ArrowUpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export default function Inventory() {
  const { t, lang } = useI18n();
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [movements, setMovements] = useState([]);
  const [productOpen, setProductOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [movOpen, setMovOpen] = useState(false);
  const [movProduct, setMovProduct] = useState(null);

  const load = useCallback(async () => {
    const [p, s, m] = await Promise.all([api.get('/products'), api.get('/suppliers'), api.get('/stock-movements')]);
    setProducts(p.data); setSuppliers(s.data); setMovements(m.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="inventory-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading font-bold">{t('nav_inventory')}</h1>
        <Button onClick={() => { setEditing(null); setProductOpen(true); }} data-testid="inventory-new-product-button"><Plus className="h-4 w-4 mr-1" /> {t('products')}</Button>
      </div>
      {products.length === 0 ? (
        <Card><CardContent className="text-center py-12">
          <Package className="h-10 w-10 text-[hsl(var(--muted-foreground))] mx-auto mb-3" />
          <p className="text-sm text-[hsl(var(--muted-foreground))]">{lang === 'es' ? 'Crea productos.' : 'Crie produtos.'}</p>
        </CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[hsl(var(--muted))]">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs uppercase">{t('name')}</th>
                    <th className="text-left px-3 py-2 text-xs uppercase">{t('category')}</th>
                    <th className="text-right px-3 py-2 text-xs uppercase">{t('stock')}</th>
                    <th className="text-right px-3 py-2 text-xs uppercase">{t('min_stock')}</th>
                    <th className="text-right px-3 py-2 text-xs uppercase">{t('unit_cost')}</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {products.map(p => {
                    const low = (p.stock_actual || 0) <= (p.stock_minimo || 0) && p.stock_minimo > 0;
                    return (
                      <tr key={p.id} className={cn('border-t hover:bg-[hsl(var(--muted))]/40', low && 'bg-[hsl(43_92%_96%)]')} data-testid={`inventory-row-${p.id}`}>
                        <td className="px-3 py-2 cursor-pointer" onClick={() => { setEditing(p); setProductOpen(true); }}>
                          <div className="font-medium">{p.nombre}</div>
                          {p.notas && <div className="text-xs text-[hsl(var(--muted-foreground))]">{p.notas}</div>}
                        </td>
                        <td className="px-3 py-2">{p.categoria || '-'}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-mono">{p.stock_actual} {p.unidad}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-mono text-[hsl(var(--muted-foreground))]">{p.stock_minimo} {p.unidad}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-mono">{formatCurrency(p.coste_por_unidad, lang)}</td>
                        <td className="px-3 py-2 text-right">
                          <Button size="sm" variant="outline" onClick={() => { setMovProduct(p); setMovOpen(true); }} data-testid={`inventory-movement-${p.id}-button`}>
                            <ArrowDownCircle className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {movements.length > 0 && (
        <Card>
          <CardContent className="p-3">
            <h3 className="font-heading font-semibold mb-2 text-sm uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{t('movement_history')}</h3>
            <div className="space-y-1.5 max-h-80 overflow-y-auto">
              {movements.slice(0, 30).map(m => {
                const p = products.find(pp => pp.id === m.product_id);
                return (
                  <div key={m.id} className="flex items-center justify-between text-sm py-1.5 border-b last:border-0">
                    <div className="flex items-center gap-2">
                      {m.tipo === 'entrada' ? <ArrowDownCircle className="h-4 w-4 text-[hsl(142_70%_45%)]" /> : <ArrowUpCircle className="h-4 w-4 text-[hsl(0_70%_50%)]" />}
                      <div>
                        <div className="font-medium">{p?.nombre || '?'}</div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))]">{formatDateTime(m.created_at, lang)} • {m.tipo} • {m.motivo || '-'}</div>
                      </div>
                    </div>
                    <Badge variant="outline" className="font-mono">{m.tipo === 'entrada' ? '+' : '-'}{m.cantidad} {p?.unidad}</Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <ProductDialog open={productOpen} onOpenChange={setProductOpen} editing={editing} suppliers={suppliers} t={t} lang={lang} onSaved={load} />
      <MovementDialog open={movOpen} onOpenChange={setMovOpen} product={movProduct} t={t} lang={lang} onSaved={load} />
    </div>
  );
}

function ProductDialog({ open, onOpenChange, editing, suppliers, t, lang, onSaved }) {
  const [form, setForm] = useState({
    nombre: '', categoria: '', unidad: 'kg', coste_por_unidad: 0, iva_compra_pct: 10,
    stock_actual: 0, stock_minimo: 0, proveedor_id: '', pgc_codigo: '601', notas: '',
  });
  useEffect(() => {
    if (editing) setForm({ ...editing, proveedor_id: editing.proveedor_id || '' });
    else setForm({ nombre: '', categoria: '', unidad: 'kg', coste_por_unidad: 0, iva_compra_pct: 10, stock_actual: 0, stock_minimo: 0, proveedor_id: '', pgc_codigo: '601', notas: '' });
  }, [editing, open]);
  const save = async () => {
    const payload = { ...form, proveedor_id: form.proveedor_id || null };
    if (editing) await api.put(`/products/${editing.id}`, payload);
    else await api.post('/products', payload);
    toast.success('OK'); onOpenChange(false); onSaved();
  };
  const remove = async () => { await api.delete(`/products/${editing.id}`); onOpenChange(false); onSaved(); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="inventory-product-dialog">
        <DialogHeader><DialogTitle>{editing ? t('edit') : t('products')}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>{t('name')}</Label><Input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} data-testid="product-name-input" /></div>
          <div><Label>{t('category')}</Label><Input value={form.categoria} onChange={e => setForm({ ...form, categoria: e.target.value })} placeholder={lang === 'es' ? 'Carne, verdura...' : 'Carne, verdura...'} /></div>
          <div><Label>{t('unit')}</Label>
            <Select value={form.unidad} onValueChange={v => setForm({ ...form, unidad: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="kg">kg</SelectItem><SelectItem value="g">g</SelectItem>
                <SelectItem value="l">l</SelectItem><SelectItem value="ml">ml</SelectItem>
                <SelectItem value="ud">unidad</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>{t('unit_cost')}</Label><Input type="number" step="0.001" value={form.coste_por_unidad} onChange={e => setForm({ ...form, coste_por_unidad: Number(e.target.value) })} data-testid="product-cost-input" /></div>
          <div><Label>IVA % compra</Label>
            <Select value={String(form.iva_compra_pct)} onValueChange={v => setForm({ ...form, iva_compra_pct: Number(v) })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="4">4%</SelectItem><SelectItem value="10">10%</SelectItem><SelectItem value="21">21%</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>{t('stock')}</Label><Input type="number" step="0.001" value={form.stock_actual} onChange={e => setForm({ ...form, stock_actual: Number(e.target.value) })} /></div>
          <div><Label>{t('min_stock')}</Label><Input type="number" step="0.001" value={form.stock_minimo} onChange={e => setForm({ ...form, stock_minimo: Number(e.target.value) })} /></div>
          <div className="col-span-2"><Label>{t('supplier_name')}</Label>
            <Select value={form.proveedor_id || 'none'} onValueChange={v => setForm({ ...form, proveedor_id: v === 'none' ? '' : v })}>
              <SelectTrigger><SelectValue placeholder="-" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">-</SelectItem>
                {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="gap-2">
          {editing && <Button variant="destructive" onClick={remove}><Trash2 className="h-4 w-4" /></Button>}
          <Button onClick={save} disabled={!form.nombre} data-testid="product-save-button">{t('save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MovementDialog({ open, onOpenChange, product, t, lang, onSaved }) {
  const [form, setForm] = useState({ tipo: 'entrada', cantidad: 0, coste_unitario: 0, motivo: '' });
  useEffect(() => { if (product) setForm({ tipo: 'entrada', cantidad: 0, coste_unitario: product.coste_por_unidad, motivo: '' }); }, [product, open]);
  if (!product) return null;
  const save = async () => {
    await api.post('/stock-movements', { product_id: product.id, ...form });
    toast.success('OK'); onOpenChange(false); onSaved();
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="inventory-movement-dialog">
        <DialogHeader><DialogTitle>{t('new_movement')} - {product.nombre}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>{lang === 'es' ? 'Tipo' : 'Tipo'}</Label>
            <Select value={form.tipo} onValueChange={v => setForm({ ...form, tipo: v })}>
              <SelectTrigger data-testid="movement-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="entrada">{t('movement_entrada')}</SelectItem>
                <SelectItem value="salida">{t('movement_salida')}</SelectItem>
                <SelectItem value="merma">{t('movement_merma')}</SelectItem>
                <SelectItem value="ajuste">{t('movement_ajuste')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>{t('quantity')} ({product.unidad})</Label><Input type="number" step="0.001" value={form.cantidad} onChange={e => setForm({ ...form, cantidad: Number(e.target.value) })} data-testid="movement-qty-input" /></div>
          {form.tipo === 'entrada' && (
            <div><Label>{t('unit_cost')}</Label><Input type="number" step="0.001" value={form.coste_unitario} onChange={e => setForm({ ...form, coste_unitario: Number(e.target.value) })} /></div>
          )}
          <div><Label>{t('notes')}</Label><Input value={form.motivo} onChange={e => setForm({ ...form, motivo: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={!form.cantidad} data-testid="movement-save-button">{t('save')}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
